// Modal para crear una exclusión de producto externo
// Permite seleccionar tienda, producto externo, variante y manejo de importe
import { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { useCreateExclusion } from '../../hooks/useExternalExcludedProducts';
import { useToast } from '@shared/hooks/use-toast';
import { ShopApiAdapter } from '@modules/Products/CreateEdit/adapters/ShopApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { getExcludedProductErrorMessage } from '../../utils/errorMessages';

export interface CreateExclusionModalRef {
  openModal: () => void;
  closeModal: () => void;
}

interface CreateExclusionModalProps {
  onSuccess?: () => void;
}

export const CreateExclusionModal = forwardRef<CreateExclusionModalRef, CreateExclusionModalProps>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('productsModule');
    const { toast } = useToast();
    const modalRef = useRef<ModalCenterRef>(null);
    const createMutation = useCreateExclusion();

    // Estados del formulario
    const [shopId, setShopId] = useState('');
    const [externalProductId, setExternalProductId] = useState('');
    const [externalVariantId, setExternalVariantId] = useState('');
    const [externalProductTitle, setExternalProductTitle] = useState('');
    const [amountHandling, setAmountHandling] = useState<'remove' | 'add_to_shipping'>('remove');

    // Estados para opciones
    const [shops, setShops] = useState<{ label: string; value: string }[]>([]);
    const [externalProducts, setExternalProducts] = useState<
      {
        value: string;
        label: string;
        variant_id?: string | number;
        variants?: Array<{ id: number; title: string }>;
      }[]
    >([]);
    const [variants, setVariants] = useState<{ label: string; value: string }[]>([]);

    // Estados de carga
    const [loadingShops, setLoadingShops] = useState(false);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [errorProducts, setErrorProducts] = useState(false);

    // Cargar tiendas al montar
    useEffect(() => {
      setLoadingShops(true);
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new ShopApiAdapter(httpClient);
      adapter
        .getAll()
        .then((data) => {
          setShops(
            data.map((shop) => ({
              value: String(shop.id),
              label: shop.name,
            })),
          );
          setLoadingShops(false);
        })
        .catch(() => {
          setLoadingShops(false);
        });
    }, []);

    // Cargar productos externos cuando se selecciona una tienda
    useEffect(() => {
      if (shopId) {
        setLoadingProducts(true);
        setErrorProducts(false);
        const httpClient = createHttpClient(apiClientWH);
        const adapter = new ShopApiAdapter(httpClient);
        adapter
          .getProductsByShop(shopId)
          .then((data) => {
            setExternalProducts(
              data.map((prod) => ({
                value: prod.id,
                label: prod.name,
                variant_id: prod.variant_id,
                variants: prod.variants || [],
              })),
            );
            setLoadingProducts(false);
            setErrorProducts(false);
          })
          .catch((error) => {
            console.log('🔍 CreateExclusionModal - Error loading products:', {
              error,
              status: error?.response?.status,
              data: error?.response?.data,
            });

            setExternalProducts([]);
            setLoadingProducts(false);
            setErrorProducts(true);

            // Obtener código de estado HTTP
            const statusCode = error?.response?.status;

            // Determinar el mensaje según el tipo de error
            let errorMessage: string;

            if (statusCode === 400) {
              // Error 400: Bad Request - mostrar mensaje genérico
              errorMessage = t('external_excluded.create.error.somethingWentWrong');
            } else if (statusCode >= 500) {
              // Errores 5xx: Internal Server Error - mostrar mensaje genérico
              errorMessage = t('external_excluded.create.error.somethingWentWrong');
            } else {
              // Otros errores: intentar mostrar mensaje específico de la API
              const data = error?.response?.data;
              errorMessage =
                data?.message ||
                data?.error?.message ||
                (Array.isArray(data?.errors) && data.errors[0]?.message) ||
                data?.error_description ||
                error?.message ||
                t('external_excluded.create.error.loadingProductsDesc');
            }

            toast({
              title: t('external_excluded.create.error.loadingProducts'),
              description: errorMessage,
              variant: 'destructive',
            });
          });
      } else {
        setExternalProducts([]);
        setErrorProducts(false);
      }
      setExternalProductId('');
      setExternalVariantId('');
      setVariants([]);
    }, [shopId, t, toast]);

    // Extraer variantes del producto seleccionado
    useEffect(() => {
      if (externalProductId) {
        const productoSeleccionado = externalProducts.find((p) => p.value === externalProductId);
        if (productoSeleccionado?.variants && productoSeleccionado.variants.length > 0) {
          setVariants(
            productoSeleccionado.variants.map((v) => ({
              value: String(v.id),
              label: v.title,
            })),
          );
        } else {
          setVariants([]);
        }
      } else {
        setVariants([]);
      }
      setExternalVariantId('');
    }, [externalProductId, externalProducts]);

    // Actualizar título del producto cuando se selecciona
    useEffect(() => {
      if (externalProductId) {
        const productoSeleccionado = externalProducts.find((p) => p.value === externalProductId);
        if (productoSeleccionado) {
          setExternalProductTitle(productoSeleccionado.label);
        }
      } else {
        setExternalProductTitle('');
      }
    }, [externalProductId, externalProducts]);

    useImperativeHandle(ref, () => ({
      openModal: () => {
        // Resetear formulario
        setShopId('');
        setExternalProductId('');
        setExternalVariantId('');
        setExternalProductTitle('');
        setAmountHandling('remove');
        setExternalProducts([]);
        setVariants([]);
        setErrorProducts(false);
        modalRef.current?.openModal();
      },
      closeModal: () => {
        modalRef.current?.closeModal();
      },
    }));

    const handleSubmit = async () => {
      // Validaciones
      if (!shopId || !externalProductId) {
        toast({
          title: t('external_excluded.create.error.validation'),
          description: t('external_excluded.create.error.requiredFields'),
          variant: 'destructive',
        });
        return;
      }

      const productoSeleccionado = externalProducts.find((p) => p.value === externalProductId);
      const tieneVariantes = productoSeleccionado?.variants && productoSeleccionado.variants.length > 0;

      if (tieneVariantes && !externalVariantId) {
        toast({
          title: t('external_excluded.create.error.validation'),
          description: t('external_excluded.create.error.variantRequired'),
          variant: 'destructive',
        });
        return;
      }

      // Determinar external_variant_id
      let finalVariantId: string;
      if (externalVariantId) {
        finalVariantId = externalVariantId;
      } else if (productoSeleccionado?.variant_id) {
        finalVariantId = String(productoSeleccionado.variant_id);
      } else {
        toast({
          title: t('external_excluded.create.error.validation'),
          description: t('external_excluded.create.error.variantRequired'),
          variant: 'destructive',
        });
        return;
      }

      try {
        console.log('🔍 CreateExclusionModal - Creating exclusion:', {
          shopId,
          externalProductId,
          externalVariantId: finalVariantId,
          amountHandling,
        });

        const payload = {
          shop_id: Number(shopId),
          external_product_id: externalProductId,
          external_variant_id: finalVariantId,
          external_shop_product_title: externalProductTitle || undefined,
          excluded_products_amount_handling: amountHandling,
        };

        const response = await createMutation.mutateAsync(payload);

        console.log('🔍 CreateExclusionModal - Create response:', response);

        if (response.success) {
          toast({
            title: t('external_excluded.create.success.title'),
            description: response.message || t('external_excluded.create.success.description'),
            variant: 'default',
          });
          modalRef.current?.closeModal();
          onSuccess?.();
        } else {
          toast({
            title: t('external_excluded.create.error.title'),
            description: response.message,
            variant: 'destructive',
          });
        }
      } catch (error: any) {
        console.log('🔍 CreateExclusionModal - Create error:', error);

        const errorMessage = getExcludedProductErrorMessage(error, t);

        toast({
          title: t('external_excluded.create.error.title'),
          description: errorMessage,
          variant: 'destructive',
        });
      }
    };

    const amountHandlingOptions = [
      {
        label: t('external_excluded.amountHandling.remove'),
        value: 'remove',
      },
      {
        label: t('external_excluded.amountHandling.addToShipping'),
        value: 'add_to_shipping',
      },
    ];

    const isFormValid = shopId && externalProductId && (variants.length === 0 || externalVariantId) && amountHandling;

    return (
      <ModalCenter
        ref={modalRef}
        title={t('external_excluded.create.title')}
        type="default"
        width="lg"
        className="h-[100dvh] sm:h-auto w-full sm:w-[500px]"
      >
        <div className="flex flex-col h-full sm:h-auto">
          <div className="space-y-4 pt-3 sm:pb-2 flex-1 overflow-y-auto">
            {/* Shop Select - Selector de tienda */}
            <div>
              <label className="block text-sm font-medium text-title mb-2">
                {t('external_excluded.create.shop')} *
              </label>
              <Combobox
                options={shops}
                value={shopId}
                setValue={setShopId}
                placeholder={t('external_excluded.create.shopPlaceholder')}
                disabled={loadingShops}
                dataTestId="create-exclusion-shop-combobox"
              />
            </div>

            {/* External Product Select - Selector de producto externo */}
            <div>
              <label className="block text-sm font-medium text-title mb-2">
                {t('external_excluded.create.externalProduct')} *
              </label>
              <Combobox
                options={externalProducts}
                value={externalProductId}
                setValue={setExternalProductId}
                placeholder={
                  loadingProducts
                    ? t('external_excluded.create.loadingProducts')
                    : errorProducts
                      ? t('external_excluded.create.error.loadingProducts')
                      : shopId
                        ? t('external_excluded.create.externalProductPlaceholder')
                        : t('external_excluded.create.selectShopFirst')
                }
                disabled={!shopId || loadingProducts}
                dataTestId="create-exclusion-product-combobox"
              />
            </div>

            {/* Variant Select - Selector de variante (solo si hay variantes) */}
            {variants.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-title mb-2">
                  {t('external_excluded.create.variant')} *
                </label>
                <Combobox
                  options={variants}
                  value={externalVariantId}
                  setValue={setExternalVariantId}
                  placeholder={t('external_excluded.create.variantPlaceholder')}
                  dataTestId="create-exclusion-variant-combobox"
                />
              </div>
            )}

            {/* Product Title Input - Input de título (opcional) */}
            <div>
              <label className="block text-sm font-medium text-title mb-2">
                {t('external_excluded.create.productTitle')}
              </label>
              <Input
                value={externalProductTitle}
                onChange={(e) => setExternalProductTitle(e.target.value)}
                placeholder={t('external_excluded.create.productTitlePlaceholder')}
                data-testid="create-exclusion-title-input"
              />
            </div>

            {/* Amount Handling Select - Selector de manejo de importe */}
            <div>
              <label className="block text-sm font-medium text-title mb-2">
                {t('external_excluded.create.amountHandling')} *
              </label>
              <Combobox
                options={amountHandlingOptions}
                value={amountHandling}
                setValue={(value) => setAmountHandling(value as 'remove' | 'add_to_shipping')}
                placeholder={t('external_excluded.create.amountHandlingPlaceholder')}
                dataTestId="create-exclusion-amount-handling-combobox"
              />
            </div>
          </div>

          {/* Actions - Acciones */}
          <div className="flex justify-start sm:justify-end gap-2 pt-2 sm:pt-4 pb-2 sm:pb-0 mt-auto sm:mt-0">
            <Button
              variant="outline"
              size="lg"
              onClick={() => modalRef.current?.closeModal()}
              disabled={createMutation.isPending}
              data-testid="create-exclusion-cancel-button"
            >
              {t('external_excluded.create.cancel')}
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={handleSubmit}
              disabled={!isFormValid || createMutation.isPending}
              data-testid="create-exclusion-submit-button"
            >
              {createMutation.isPending ? t('external_excluded.create.creating') : t('external_excluded.create.submit')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

CreateExclusionModal.displayName = 'CreateExclusionModal';
